// DevHub CUSTOM — i18n "mcp" namespace (MCP catalog, access page, token manager).
// See ./common.ts for the convention.
import { createTranslationRef } from '@backstage/core-plugin-api/alpha';

export const mcpMessages = createTranslationRef({
  id: 'devhub.mcp',
  messages: {
    catalog: {
      eyebrow: 'Resources · MCP Catalog',
      title: 'MCP Marketplace',
      intro:
        'Browse Model Context Protocol servers across Dräger. Open a server to see what it exposes and how to connect it.',
      verifiedOnly: 'Verified only',
      verifiedBadge: 'Verified',
      search: {
        servers: 'Search servers…',
      },
      tags: {
        all: 'All',
      },
      loading: 'Loading…',
      noServers: 'No servers match your search.',
      error: 'Could not load the MCP catalog. Please try again later.',
      card: {
        getStarted: 'Get started',
        viewDetails: 'View details',
      },
    },
    detail: {
      back: 'MCP Catalog',
      notFound: 'Server not found.',
      firstParty: 'First-party',
      community: 'Community',
      internal: 'Internal',
      getAccess: 'Get access',
      getAccessBody:
        'Requires a Personal Access Token (scoped to the products you’re entitled to). Create one, then use a snippet below to add the server to your assistant.',
      getToken: 'Get a Personal Access Token →',
      connect: 'Connect',
      connectBtn: 'Connect',
      referenceListing: 'Reference listing published by {{team}}.',
      referenceListingContact: ' Contact {{contact}} for access and setup.',
      referenceListingLinks: ' See the links for setup.',
      anotherTeam: 'another team',
      tools: 'Tools & capabilities',
      details: 'Details',
      team: 'Team',
      exposes: 'Exposes',
      auth: 'Auth',
      authPat: 'Personal Access Token',
      authNone: 'None',
      authDocs: 'See docs',
      tags: 'Tags',
      contact: 'Contact',
      links: 'Links',
      repository: 'Repository ↗',
      docs: 'Docs ↗',
    },
    access: {
      back: 'Partner Portal',
      eyebrow: 'MCP Access',
      title: 'Bring Dräger product docs into your AI assistant',
      lead:
        'The Dräger DevHub MCP server connects the documentation for your entitled products directly to Claude Code and any other MCP-compatible AI assistant — so you can ask, build, and integrate without leaving your editor.',
      whatTitle: 'What is the DevHub MCP?',
      whatBody:
        'MCP (Model Context Protocol) is an open standard that lets AI assistants securely pull in live, authoritative context. The DevHub MCP server exposes Dräger’s API references, integration guides, and product documentation as a tool your assistant can read on demand — always up to date, never copy-pasted.',
      doTitle: 'What you can do with it',
      do1: 'Ask your assistant questions about Dräger APIs and get answers grounded in the official docs.',
      do2: 'Generate integration code and configuration against the real, current specifications.',
      do3: 'Troubleshoot errors with the relevant reference material already in context.',
      do4: 'Keep your assistant’s knowledge current — docs update server-side, no re-syncing.',
      scopedTitle: 'Scoped to your entitlements',
      scopedBody:
        'A token you create can only read the products you have premium access to — nothing more. It authenticates as you and can be revoked at any time. Your current entitlements are shown below.',
      tokensTitle: 'Your access tokens',
      // Legacy keys (kept for compatibility with any external reference).
      intro1: 'Connect the ',
      intro2: 'Dräger DevHub MCP server',
      intro3:
        ' to Claude Code (or any MCP client) so your AI assistant can read the API documentation for the products you’re entitled to.',
    },
    settings: {
      title: 'MCP Personal Access Tokens',
      intro1:
        'Create tokens to connect the Dräger DevHub MCP server to Claude Code (or any MCP client). A token authenticates as you and is scoped to the products you’re entitled to. Browse available servers in the ',
      catalogLink: 'MCP Catalog',
      intro2: '.',
    },
    manager: {
      entitlements: 'Your entitlements',
      role: 'Role:',
      canRead: 'Tokens you create can read docs for:',
      fullInternal:
        'As a Dräger employee, tokens you create have full access to the internal documentation across the DevHub.',
      fullInternalShort: 'Full internal access',
      noEntitlements:
        'You currently have no product entitlements, so you cannot create a token. Contact your Dräger representative for product access.',
      gatedTitle: 'Token creation is locked',
      gatedBody:
        'Personal Access Tokens require a product entitlement. You don’t have access to any products yet, so there’s nothing a token could read. Contact your Dräger representative to request product access, then return here to create one.',
      createTitle: 'Create a token',
      nameLabel: 'Token name',
      namePlaceholder: 'e.g. Claude Code on my laptop',
      expiresLabel: 'Expires in (days, optional)',
      expiresPlaceholder: 'never',
      creating: 'Creating…',
      createBtn: 'Create token',
      errNoName: 'Please give the token a name.',
      errCreate: 'Failed to create token.',
      created: 'Token created — copy it now',
      createdOnce:
        'This is the only time the full token is shown. Store it somewhere safe; if you lose it, revoke it and create a new one.',
      copy: 'Copy',
      copied: 'Copied!',
      connectClaudeCode: 'Connect it to Claude Code',
      yourTokens: 'Your tokens',
      loading: 'Loading…',
      noTokens: 'No tokens yet.',
      colName: 'Name',
      colPrefix: 'Prefix',
      colProducts: 'Products',
      colCreated: 'Created',
      colLastUsed: 'Last used',
      colExpires: 'Expires',
      revoke: 'Revoke',
      revokeTitle: 'Revoke this token?',
      revokeMessage: ' will stop working immediately and any MCP client using it will lose access. This cannot be undone.',
      revokeConfirm: 'Revoke',
      toastCreated: 'Token "{{name}}" created — copy it now.',
      toastRevoked: 'Token "{{name}}" revoked.',
      toastRevokeFailed: 'Failed to revoke token.',
    },
    connect: {
      // Quick-connect banner (token manager)
      quickTitle: 'Quick connect',
      quickBody:
        'Mint a token and get a ready-to-paste snippet for your AI tool in one step.',
      quickBtn: 'Connect your AI tool',
      // Dialog
      title: 'Connect your AI tool',
      lead:
        'Create a token and drop a ready-to-paste snippet into Claude Code, Cursor, Claude Desktop, or VS Code — the token is filled in for you.',
      mintBody:
        'This creates a Personal Access Token scoped to the products you’re entitled to. The full token is shown once, right here.',
      connectBtn: 'Connect',
      minting: 'Connecting…',
      errMint: 'Could not create a token. Please try again.',
      secretTitle: 'Token created',
      secretWarn:
        'Copy it now — for your security the full token is shown only once and won’t be displayed again.',
      tokenLabel: 'Token',
      snippetsTitle: 'Add it to your AI tool',
      snippetsHint:
        'Pick your tool, copy the snippet, and paste it in. Each snippet already includes your token.',
      clientClaudeCode: 'Claude Code',
      clientCursor: 'Cursor',
      clientClaudeDesktop: 'Claude Desktop',
      clientVscode: 'VS Code',
    },
  },
  translations: {
    de: () => import('./de/mcp'),
  },
});
